import React, { useEffect, useMemo, useState } from "react";
import { Badge, Box, Container, Group, Paper, Select, SimpleGrid, Stack, Text, Title } from "@mantine/core";
import CheckAddressBackground from "../../checkBitcoinAddress/components/CheckAddressBackground";
import RizfulPitch from "../../rizfulPitch/RizfulPitch";
import ServerRenderedBlock, { hasServerRenderedBlock } from "../../helpers/ServerRenderedBlock";
import FeeCalculator from "./FeeCalculator";
import {
  FeeCurrency,
  FeeData,
  agoLabel,
  bandFor,
  fetchFeeData,
  formatSats,
  nextHalving,
  rateLabel,
  satsFor,
  satsToMoney,
} from "../helpers/feeMath";
import ToolLogo from "../../helpers/ToolLogo";

// On-chain fees only: this page never shows Lightning costs or compares the two.

const PAGE_PATH = "/bitcoin-fees";
const POLL_MS = 30_000;
const CURRENCY_KEY = "rz-fee-currency";
const PANEL_STYLE: React.CSSProperties = {
  backgroundColor: "rgba(0, 0, 0, 0.35)",
  border: "1px solid rgba(255, 255, 255, 0.1)",
};
const NUMBERS: React.CSSProperties = { fontVariantNumeric: "tabular-nums" };

const readInitialData = (): FeeData | null => {
  try {
    const raw = document.getElementById("root")?.dataset.hydrate;
    return raw ? (JSON.parse(raw) as FeeData) : null;
  } catch (_e) {
    return null;
  }
};

const storedCurrency = (): string | null => {
  try {
    return window.localStorage.getItem(CURRENCY_KEY);
  } catch (_e) {
    return null;
  }
};

const rememberCurrency = (code: string) => {
  try {
    window.localStorage.setItem(CURRENCY_KEY, code);
  } catch (_e) {
    // private mode etc.: the choice just isn't remembered
  }
};

// ?currency= (share links) wins, then the visitor's last choice, then the server default
const initialCurrency = (data: FeeData | null): string => {
  const fromUrl = new URLSearchParams(window.location.search).get("currency")?.toLowerCase();
  const known = (code?: string | null) => !!code && !!data?.currencies.some((c) => c.code === code);
  if (known(fromUrl)) return fromUrl!;
  const stored = storedCurrency();
  if (known(stored)) return stored!;
  return data?.currency || "usd";
};

const PulseStyles = () => (
  <style>{`
    @keyframes rz-fee-pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.25; } }
    .rz-fee-dot { display: inline-block; width: 8px; height: 8px; border-radius: 50%; background: #4ade80; animation: rz-fee-pulse 2s ease-in-out infinite; }
    .rz-fee-dot.is-stale { background: #fbbf24; animation: none; }
    @media (prefers-reduced-motion: reduce) { .rz-fee-dot { animation: none; } }
  `}</style>
);

const Tile: React.FC<{ label: string; value: React.ReactNode; note?: React.ReactNode }> = ({ label, value, note }) => (
  <Paper p="md" radius="md" style={PANEL_STYLE}>
    <Text size="xs" c="dimmed" tt="uppercase" fw={700} style={{ letterSpacing: "0.05em" }}>
      {label}
    </Text>
    <Text c="white" fw={700} size="xl" style={NUMBERS}>
      {value}
    </Text>
    {note && (
      <Text size="xs" c="dimmed" style={NUMBERS}>
        {note}
      </Text>
    )}
  </Paper>
);

const BitcoinFeesContainer: React.FC = () => {
  const [data, setData] = useState<FeeData | null>(readInitialData);
  const [currencyCode, setCurrencyCode] = useState(() => initialCurrency(data));
  // server clock minus ours, so "N s ago" is right even when the visitor's clock is off
  const [clockOffset, setClockOffset] = useState(() => (data ? data.server_time * 1000 - Date.now() : 0));
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const tick = window.setInterval(() => setNow(Date.now()), 1000);
    const poll = window.setInterval(async () => {
      if (document.hidden) return;
      const fresh = await fetchFeeData();
      if (fresh) {
        setData(fresh);
        setClockOffset(fresh.server_time * 1000 - Date.now());
      }
    }, POLL_MS);
    // a tab that comes back after a while refreshes at once
    const onVisible = async () => {
      if (document.hidden) return;
      const fresh = await fetchFeeData();
      if (fresh) {
        setData(fresh);
        setClockOffset(fresh.server_time * 1000 - Date.now());
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.clearInterval(tick);
      window.clearInterval(poll);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);

  const currency: FeeCurrency | undefined = useMemo(
    () => data?.currencies.find((c) => c.code === currencyCode) ?? data?.currencies[0],
    [data, currencyCode],
  );

  const chooseCurrency = (code: string | null) => {
    if (!code) return;
    setCurrencyCode(code);
    rememberCurrency(code);
    const url = code === "usd" ? PAGE_PATH : `${PAGE_PATH}?currency=${code}`;
    window.history.replaceState(null, "", url);
  };

  const serverNow = (now + clockOffset) / 1000;
  const tiers = data?.tiers ?? null;
  const fast = tiers?.[0];
  const vbytes = data?.typical_vbytes ?? 141;
  const feesAge = data?.fees_updated_at ? serverNow - data.fees_updated_at : null;
  const stale = feesAge === null || feesAge > 180;
  const band = fast ? bandFor(fast.rate) : null;
  const halving = data?.block_height ? nextHalving(data.block_height) : null;

  return (
    <CheckAddressBackground>
      <PulseStyles />
      <Container size={760} px="md" py="xl">
        <Stack gap="lg">
          <ToolLogo />

          {hasServerRenderedBlock("ssr-fee-head") ? (
            <ServerRenderedBlock id="ssr-fee-head" />
          ) : (
            <Box ta="center">
              <Title order={1} c="white" mb="xs">
                Bitcoin Transaction Fees Right Now
              </Title>
            </Box>
          )}

          {fast && currency && tiers ? (
            <>
              <Paper p={{ base: "md", sm: "lg" }} radius="md" style={PANEL_STYLE}>
                <Stack gap={6} align="center" ta="center">
                  <Text c="dimmed">Sending bitcoin right now costs about</Text>
                  <Text
                    c="orange.4"
                    fw={700}
                    lh={1.15}
                    style={{ ...NUMBERS, fontSize: "clamp(2.4rem, 10vw, 3.4rem)", minWidth: "6ch" }}
                  >
                    {satsToMoney(satsFor(fast.rate, vbytes), currency)}
                  </Text>
                  <Text size="sm" c="dimmed" style={NUMBERS}>
                    {formatSats(satsFor(fast.rate, vbytes))} sats at {rateLabel(fast.rate)} sat/vB for a typical {vbytes} vB
                    payment, to confirm in the next block (about 10 minutes).
                  </Text>
                  <Group gap="sm" justify="center" mt={6} wrap="wrap">
                    {band && (
                      <Badge color={band.color} variant="light" size="lg">
                        Fees: {band.label}
                      </Badge>
                    )}
                    <Select
                      aria-label="Currency"
                      value={currency.code}
                      onChange={chooseCurrency}
                      data={data!.currencies.map((c) => ({ value: c.code, label: `${c.code.toUpperCase()} · ${c.name}` }))}
                      searchable
                      allowDeselect={false}
                      size="xs"
                      w={230}
                      comboboxProps={{ withinPortal: true }}
                      nothingFoundMessage="No such currency"
                    />
                  </Group>
                  <Group gap={8} justify="center" mt={4}>
                    <span className={`rz-fee-dot${stale ? " is-stale" : ""}`} aria-hidden="true" />
                    <Text size="xs" c="dimmed" style={{ ...NUMBERS, minWidth: "13ch", textAlign: "left" }}>
                      {feesAge === null ? "live" : `updated ${agoLabel(feesAge)}`}
                    </Text>
                  </Group>
                </Stack>
              </Paper>

              <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="sm">
                {tiers.map((tier) => {
                  const sats = satsFor(tier.rate, vbytes);
                  return (
                    <Paper key={tier.key} p="md" radius="md" style={PANEL_STYLE}>
                      <Text c="white" fw={700} size="sm">
                        {tier.label}
                      </Text>
                      <Text size="xs" c="dimmed" mb={8}>
                        {tier.wait}
                      </Text>
                      <Text c="orange.4" fw={700} size="xl" style={NUMBERS}>
                        {satsToMoney(sats, currency)}
                      </Text>
                      <Text size="xs" c="dimmed" style={NUMBERS}>
                        {rateLabel(tier.rate)} sat/vB · {formatSats(sats)} sats
                      </Text>
                    </Paper>
                  );
                })}
              </SimpleGrid>
              <Text size="xs" c="dimmed" mt={-8}>
                Costs are for a typical payment: 1 input, 2 outputs, Native SegWit ({vbytes} vB).
                {data?.minimum ? ` Lowest rate most nodes relay right now: ${rateLabel(data.minimum)} sat/vB.` : ""} Fee
                bands: very low under 1 sat/vB, low under 5, normal under 20, busy under 50.
              </Text>
            </>
          ) : (
            <Paper p="md" radius="md" style={PANEL_STYLE}>
              <Text c="dimmed">Live fee data is unavailable right now. Please try again in a minute.</Text>
            </Paper>
          )}

          <SimpleGrid cols={{ base: 1, xs: 3 }} spacing="sm">
            {data?.block_height && <Tile label="Block height" value={formatSats(data.block_height)} />}
            {data?.block_time && (
              <Tile
                label="Last block"
                value={agoLabel(serverNow - data.block_time)}
                note="Blocks come about every 10 minutes, at random"
              />
            )}
            {halving && (
              <Tile
                label="Next halving"
                value={`~${halving.estimatedAt.toLocaleDateString("en-US", { month: "short", year: "numeric" })}`}
                note={`Block ${formatSats(halving.height)} · ${formatSats(halving.blocksLeft)} blocks to go`}
              />
            )}
          </SimpleGrid>

          {hasServerRenderedBlock("ssr-fee-chart") && <ServerRenderedBlock id="ssr-fee-chart" />}

          {tiers && currency && <FeeCalculator tiers={tiers} currency={currency} panelStyle={PANEL_STYLE} />}

          {hasServerRenderedBlock("ssr-fee-guide") && <ServerRenderedBlock id="ssr-fee-guide" />}

          <RizfulPitch
            lead="Need a bitcoin wallet that's simple to start with? Rizful is free and runs in your browser."
            subhead="No install, no subscription."
            features={[
              "Your own @rizful.com Lightning Address",
              "Receive Bitcoin payments",
              "Connect hundreds of apps with NWC",
              "Works in any browser, phone or desktop",
            ]}
          />
          <ServerRenderedBlock id="ssr-tool-footer" />
        </Stack>
      </Container>
    </CheckAddressBackground>
  );
};

export default BitcoinFeesContainer;
